'use client';
import React, { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import ProgramBuilderClient from './components/ProgramBuilderClient';
import { buildProgramFromInputs } from '@/lib/programBuilder';
import type { ProgramBuilderInputs, TrainingFrequency, PrimaryWeakness, ReadinessStatus, ProgramDuration } from '@/lib/programBuilder';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dumbbell, ClipboardList, CheckCircle2, AlertCircle, ChevronDown,
  User, Zap, Activity, Flame, Settings2, Clock, BarChart2
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const EQUIPMENT_OPTIONS = [
  'Barbell', 'Dumbbells', 'Sled', 'Med Ball', 'Resistance Bands',
  'Pull-Up Bar', 'Plyo Boxes', 'Hurdles', 'Kettlebells', 'Cable Machine',
];

const TRAINING_AGE_OPTIONS = [
  { value: 'beginner', label: 'Beginner (< 1 year)' },
  { value: 'intermediate', label: 'Intermediate (1–3 years)' },
  { value: 'advanced', label: 'Advanced (3–5 years)' },
  { value: 'elite', label: 'Elite (5+ years)' },
];

const SESSION_LENGTH_OPTIONS = [
  '30–45 min', '45–60 min', '60–75 min', '75–90 min', '90–120 min',
];

function SelectField({ label, value, onChange, options, icon: Icon }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  icon?: React.ElementType;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">
        {Icon && <Icon size={11} className="inline mr-1.5 text-zinc-500" />}
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full bg-zinc-800/80 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm text-zinc-200 appearance-none focus:outline-none focus:border-red-600/60 transition-colors"
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, placeholder, icon: Icon }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  icon?: React.ElementType;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5 uppercase tracking-wider">
        {Icon && <Icon size={11} className="inline mr-1.5 text-zinc-500" />}
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-zinc-800/80 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-red-600/60 transition-colors"
      />
    </div>
  );
}

function FrequencySelector({ value, onChange }: { value: TrainingFrequency; onChange: (v: TrainingFrequency) => void }) {
  const options: { freq: TrainingFrequency; label: string; desc: string }[] = [
    { freq: 2, label: '2 days', desc: 'In-season / Low availability' },
    { freq: 3, label: '3 days', desc: 'Balanced split' },
    { freq: 4, label: '4 days', desc: 'Emphasis days' },
    { freq: 5, label: '5 days', desc: 'Advanced model' },
  ];
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wider">
        <Clock size={11} className="inline mr-1.5 text-zinc-500" />
        Training Days Per Week
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {options.map(o => (
          <button
            key={o.freq}
            type="button"
            onClick={() => onChange(o.freq)}
            className="flex flex-col items-center px-3 py-3 rounded-xl border text-center transition-all duration-150"
            style={value === o.freq ? {
              background: 'rgba(225,6,0,0.12)',
              borderColor: 'rgba(225,6,0,0.5)',
              color: '#E10600',
            } : {
              background: 'rgba(255,255,255,0.03)',
              borderColor: 'rgba(255,255,255,0.08)',
              color: '#71717a',
            }}
          >
            <span className="text-lg font-bold font-mono">{o.freq}</span>
            <span className="text-xs font-medium mt-0.5">{o.label}</span>
            <span className="text-xs mt-0.5 opacity-70">{o.desc}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function WeaknessSelector({ value, onChange }: { value: PrimaryWeakness; onChange: (v: PrimaryWeakness) => void }) {
  const options: { val: PrimaryWeakness; label: string; desc: string; icon: React.ElementType; color: string }[] = [
    { val: 'engine', label: 'Engine', desc: 'Max strength / force production', icon: Flame, color: '#8B0000' },
    { val: 'ignition', label: 'Ignition', desc: 'Rate of force / explosiveness', icon: Zap, color: '#E10600' },
    { val: 'bounce', label: 'Bounce', desc: 'Elastic stiffness / reactive power', icon: Activity, color: '#B3B3B3' },
  ];
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wider">
        Primary Weakness
      </label>
      <div className="grid grid-cols-3 gap-2">
        {options.map(o => {
          const Icon = o.icon;
          const active = value === o.val;
          return (
            <button
              key={o.val}
              type="button"
              onClick={() => onChange(o.val)}
              className="flex flex-col items-center px-2 py-3 rounded-xl border text-center transition-all duration-150"
              style={active ? {
                background: `${o.color}18`,
                borderColor: `${o.color}60`,
              } : {
                background: 'rgba(255,255,255,0.03)',
                borderColor: 'rgba(255,255,255,0.08)',
              }}
            >
              <Icon size={16} style={{ color: active ? o.color : '#52525b' }} className="mb-1" />
              <span className="text-xs font-semibold" style={{ color: active ? o.color : '#71717a' }}>{o.label}</span>
              <span className="text-xs mt-0.5 text-zinc-600 leading-tight">{o.desc}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReadinessSelector({ value, onChange }: { value: ReadinessStatus; onChange: (v: ReadinessStatus) => void }) {
  const options: { val: ReadinessStatus; label: string; color: string }[] = [
    { val: 'high', label: 'High', color: '#10B981' },
    { val: 'moderate', label: 'Moderate', color: '#F59E0B' },
    { val: 'low', label: 'Low', color: '#EF4444' },
  ];
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wider">
        Wellness / Readiness Status
      </label>
      <div className="flex gap-2">
        {options.map(o => (
          <button
            key={o.val}
            type="button"
            onClick={() => onChange(o.val)}
            className="flex-1 py-2 rounded-xl border text-xs font-semibold transition-all duration-150"
            style={value === o.val ? {
              background: `${o.color}18`,
              borderColor: `${o.color}60`,
              color: o.color,
            } : {
              background: 'rgba(255,255,255,0.03)',
              borderColor: 'rgba(255,255,255,0.08)',
              color: '#71717a',
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function EquipmentSelector({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (eq: string) => {
    onChange(value.includes(eq) ? value.filter(e => e !== eq) : [...value, eq]);
  };
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wider">
        Available Equipment
      </label>
      <div className="flex flex-wrap gap-2">
        {EQUIPMENT_OPTIONS.map(eq => {
          const active = value.includes(eq);
          return (
            <button
              key={eq}
              type="button"
              onClick={() => toggle(eq)}
              className="px-3 py-1.5 rounded-lg border text-xs font-medium transition-all duration-150"
              style={active ? {
                background: 'rgba(225,6,0,0.12)',
                borderColor: 'rgba(225,6,0,0.4)',
                color: '#E10600',
              } : {
                background: 'rgba(255,255,255,0.03)',
                borderColor: 'rgba(255,255,255,0.08)',
                color: '#71717a',
              }}
            >
              {eq}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TrainingProgramBuilderContent() {
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const queryAthleteId = searchParams.get('athleteId');

  const [athleteSession, setAthleteSession] = useState<any>(null);
  const [athleteName, setAthleteName] = useState('');
  const [athleteId, setAthleteId] = useState<string | null>(null);
  const [athleteUserId, setAthleteUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [savedProgramId, setSavedProgramId] = useState<string | null>(null);

  // Program builder inputs
  const [frequency, setFrequency] = useState<TrainingFrequency>(4);
  const [sport, setSport] = useState('');
  const [position, setPosition] = useState('');
  const [trainingAge, setTrainingAge] = useState('intermediate');
  const [primaryWeakness, setPrimaryWeakness] = useState<PrimaryWeakness>('engine');
  const [equipment, setEquipment] = useState<string[]>(['Barbell', 'Dumbbells', 'Sled', 'Med Ball', 'Plyo Boxes']);
  const [sessionLength, setSessionLength] = useState('60–75 min');
  const [readiness, setReadiness] = useState<ReadinessStatus>('high');
  const [duration, setDuration] = useState<ProgramDuration>(8);
  const [programGenerated, setProgramGenerated] = useState(false);
  const [generatedProgram, setGeneratedProgram] = useState<ReturnType<typeof buildProgramFromInputs> | null>(null);

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        let resolvedAthleteId: string | null = null;
        let resolvedAthleteUserId: string | null = null;
        let resolvedAthleteName = '';

        if (queryAthleteId) {
          const { data: ap, error: apErr } = await supabase
            .from('athlete_profiles')
            .select('id, user_id, name')
            .eq('id', queryAthleteId)
            .single();

          if (apErr || !ap) {
            setLoadError('Athlete not found. Please go back and select an athlete.');
            setLoading(false);
            return;
          }

          const { data: link } = await supabase
            .from('coach_athletes')
            .select('id')
            .eq('coach_id', user.id)
            .eq('athlete_id', queryAthleteId)
            .eq('status', 'active')
            .single();

          if (!link) {
            setLoadError('Permission denied — you are not assigned to this athlete.');
            setLoading(false);
            return;
          }

          resolvedAthleteId = ap.id;
          resolvedAthleteUserId = ap.user_id;
          resolvedAthleteName = ap.name ?? '';
        } else {
          const { data: ap } = await supabase
            .from('athlete_profiles')
            .select('id, user_id, name')
            .eq('user_id', user.id)
            .single();

          if (ap) {
            resolvedAthleteId = ap.id;
            resolvedAthleteUserId = ap.user_id;
            resolvedAthleteName = ap.name ?? '';
          }
        }

        setAthleteId(resolvedAthleteId);
        setAthleteUserId(resolvedAthleteUserId);
        setAthleteName(resolvedAthleteName);

        if (resolvedAthleteId) {
          const { data: sessionData } = await supabase
            .from('test_sessions')
            .select('id, engine_score, ignition_score, bounce_score, eps, session_date, sport, position')
            .eq('athlete_id', resolvedAthleteId)
            .order('session_date', { ascending: false })
            .limit(1)
            .single();

          if (sessionData) {
            setAthleteSession({
              id: sessionData.id,
              engineScore: Number(sessionData.engine_score),
              ignitionScore: Number(sessionData.ignition_score),
              bounceScore: Number(sessionData.bounce_score),
              eps: Number(sessionData.eps),
            });
            // Auto-fill from assessment
            if (sessionData.sport) setSport(sessionData.sport);
            if (sessionData.position) setPosition(sessionData.position);
            // Auto-detect primary weakness
            const e = Number(sessionData.engine_score);
            const ig = Number(sessionData.ignition_score);
            const b = Number(sessionData.bounce_score);
            const minScore = Math.min(e, ig, b);
            if (minScore === e) setPrimaryWeakness('engine');
            else if (minScore === ig) setPrimaryWeakness('ignition');
            else setPrimaryWeakness('bounce');
          }
        }
      } catch (err: any) {
        setLoadError(err.message ?? 'Failed to load program builder.');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [user, queryAthleteId]);

  const handleGenerateProgram = () => {
    const inputs: ProgramBuilderInputs = {
      frequency,
      sport,
      position,
      trainingAge,
      primaryWeakness,
      availableEquipment: equipment,
      sessionLength,
      currentEps: athleteSession?.eps ?? 0,
      readiness,
      engineScore: athleteSession?.engineScore ?? 0,
      ignitionScore: athleteSession?.ignitionScore ?? 0,
      bounceScore: athleteSession?.bounceScore ?? 0,
    };
    const program = buildProgramFromInputs(inputs, duration);
    setGeneratedProgram(program);
    setProgramGenerated(true);
    setSaveSuccess(false);
    setSaveError(null);
    setSavedProgramId(null);
  };

  const handleSaveProgram = async () => {
    if (!generatedProgram || !athleteId || !user) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const supabase = createClient();
      const targetUserId = athleteUserId ?? user.id;

      const { data: savedProgram, error: progErr } = await supabase
        .from('training_programs')
        .insert({
          athlete_id: athleteId,
          user_id: targetUserId,
          session_id: athleteSession?.id ?? null,
          focus: generatedProgram.focus,
          title: generatedProgram.title,
          rationale: generatedProgram.rationale,
          duration_weeks: duration,
          days_per_week: frequency,
          program_data: generatedProgram.weeks as any,
          progression_model: generatedProgram.progressionModel as any,
          retest_recommendation: generatedProgram.retestRecommendation,
        })
        .select('id')
        .single();

      if (progErr) {
        if (progErr.code === '42501') throw new Error('Permission denied — RLS blocked program creation. Verify coach-athlete assignment.');
        throw new Error(`Program creation failed — ${progErr.message}`);
      }
      if (!savedProgram?.id) throw new Error('Program was not saved — no ID returned.');

      setSavedProgramId(savedProgram.id);

      const { error: assignErr } = await supabase
        .from('athlete_program_assignments')
        .insert({
          coach_id: user.id,
          athlete_id: athleteId,
          program_id: savedProgram.id,
          program_title: generatedProgram.title,
          program_focus: generatedProgram.focus,
          program_data: generatedProgram.weeks as any,
          status: 'active',
          start_date: new Date().toISOString().split('T')[0],
        });

      if (assignErr) {
        if (assignErr.code === '42501') throw new Error('Program saved but assignment failed — RLS blocked. Check coach-athlete link.');
        throw new Error(`Program assignment failed — ${assignErr.message}`);
      }

      setSaveSuccess(true);
    } catch (err: any) {
      setSaveError(err.message ?? 'An unexpected error occurred while saving the program.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout activePath="/training-program-builder">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-3">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-zinc-500">Loading program builder...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (loadError) {
    return (
      <AppLayout activePath="/training-program-builder">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-3 max-w-sm mx-auto">
            <AlertCircle size={32} className="mx-auto text-red-500" />
            <p className="text-sm text-zinc-300 font-medium">{loadError}</p>
            <a href="/coach-dashboard" className="btn-primary inline-flex items-center gap-2 text-sm">
              ← Back to Dashboard
            </a>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activePath="/training-program-builder">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="page-title">Training Program Builder</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {athleteName
                ? `${athleteName} · Biotensegrity-based frequency-specific program`
                : 'Biotensegrity-based frequency-specific program'}
            </p>
          </div>
          {queryAthleteId && (
            <a href="/coach-dashboard" className="btn-ghost text-sm">← Back to Dashboard</a>
          )}
        </div>

        {/* EPS Summary (if assessment exists) */}
        {athleteSession && (
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Engine', score: athleteSession.engineScore, color: '#8B0000' },
              { label: 'Ignition', score: athleteSession.ignitionScore, color: '#E10600' },
              { label: 'Bounce', score: athleteSession.bounceScore, color: '#B3B3B3' },
            ].map(cat => (
              <div key={cat.label} className="rounded-xl p-3 border" style={{ background: `${cat.color}10`, borderColor: `${cat.color}30` }}>
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{cat.label}</p>
                <p className="font-mono font-bold text-2xl" style={{ color: cat.color }}>{cat.score}</p>
                <p className="text-xs text-zinc-500 mt-0.5">EPS: {athleteSession.eps}</p>
              </div>
            ))}
          </div>
        )}

        {/* Builder Inputs */}
        <div className="card p-5 space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <Settings2 size={16} style={{ color: '#E10600' }} />
            <h2 className="text-sm font-semibold text-zinc-200 uppercase tracking-wider">Program Builder Inputs</h2>
          </div>

          <FrequencySelector value={frequency} onChange={setFrequency} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField label="Athlete Sport" value={sport} onChange={setSport} placeholder="e.g. Baseball, Football, Soccer" icon={User} />
            <InputField label="Position" value={position} onChange={setPosition} placeholder="e.g. Pitcher, Wide Receiver" icon={User} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectField
              label="Training Age"
              value={trainingAge}
              onChange={setTrainingAge}
              options={TRAINING_AGE_OPTIONS}
              icon={BarChart2}
            />
            <SelectField
              label="Session Length"
              value={sessionLength}
              onChange={setSessionLength}
              options={SESSION_LENGTH_OPTIONS.map(s => ({ value: s, label: s }))}
              icon={Clock}
            />
          </div>

          <WeaknessSelector value={primaryWeakness} onChange={setPrimaryWeakness} />
          <ReadinessSelector value={readiness} onChange={setReadiness} />
          <EquipmentSelector value={equipment} onChange={setEquipment} />

          {/* Duration */}
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-2 uppercase tracking-wider">
              Program Duration
            </label>
            <div className="flex gap-2">
              {([4, 6, 8, 12] as ProgramDuration[]).map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(d)}
                  className="flex-1 py-2 rounded-xl border text-sm font-mono font-semibold transition-all duration-150"
                  style={duration === d ? {
                    background: 'rgba(225,6,0,0.12)',
                    borderColor: 'rgba(225,6,0,0.5)',
                    color: '#E10600',
                  } : {
                    background: 'rgba(255,255,255,0.03)',
                    borderColor: 'rgba(255,255,255,0.08)',
                    color: '#71717a',
                  }}
                >
                  {d}w
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <button
            type="button"
            onClick={handleGenerateProgram}
            className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200"
            style={{
              background: 'linear-gradient(135deg, #E10600, #8B0000)',
              color: '#ffffff',
              boxShadow: '0 0 20px rgba(225,6,0,0.3)',
            }}
          >
            <Dumbbell size={15} className="inline mr-2" />
            Generate {frequency}-Day Program
          </button>
        </div>

        {/* Errors / Success */}
        {saveError && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-950/40 border border-red-800/50">
            <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{saveError}</p>
          </div>
        )}
        {saveSuccess && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-green-950/40 border border-green-800/50">
            <CheckCircle2 size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-300">
              Program saved and assigned to {athleteName || 'athlete'} successfully.
              {savedProgramId && <span className="text-green-500 ml-1 font-mono text-xs">ID: {savedProgramId.slice(0, 8)}…</span>}
            </p>
          </div>
        )}

        {/* No Assessment Warning */}
        {!athleteSession && !programGenerated && (
          <div className="rounded-2xl p-10 text-center" style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(225,6,0,0.1)', border: '1px solid rgba(225,6,0,0.2)' }}>
              <ClipboardList size={24} style={{ color: '#E10600' }} />
            </div>
            <h3 className="text-base font-semibold text-zinc-200 mb-2">No Assessment Data Found</h3>
            <p className="text-sm text-zinc-500 mb-5 max-w-sm mx-auto">
              {queryAthleteId
                ? `No completed assessment found for ${athleteName || 'this athlete'}. You can still build a program manually using the inputs above.`
                : 'Complete an assessment first for auto-filled scores, or fill in the inputs above to build manually.'}
            </p>
            <a href="/assessment-entry" className="btn-primary inline-flex items-center gap-2 text-sm">
              <ClipboardList size={14} />
              {queryAthleteId ? 'Go to Assessment Entry' : 'Start Assessment'}
            </a>
          </div>
        )}

        {/* Generated Program */}
        {programGenerated && generatedProgram && (
          <ProgramBuilderClient
            program={generatedProgram}
            athleteName={athleteName}
            athleteId={athleteId}
            isSaving={isSaving}
            saveSuccess={saveSuccess}
            savedProgramId={savedProgramId}
            onSaveProgram={handleSaveProgram}
            onRegenerateProgram={handleGenerateProgram}
          />
        )}
      </div>
    </AppLayout>
  );
}

export default function TrainingProgramBuilderPage() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      }
    >
      <TrainingProgramBuilderContent />
    </Suspense>
  );
}